/**
 * Build DAG nodes, edges, and edge flows from a packing operation's tasks.
 * Used by OperationDetailScreen to show supply flow and persist quantity edits.
 */

import type { LpEdgeFlowRow } from './packingTaskGenerator';
import type { PackingOperation, PackingTask } from '../schema';
import { filterValidPackingTaskEndpoints } from './instantDbGuards';

export type OperationDagNodeRow = {
    index: number;
    kind: 'source' | 'event' | 'dest';
    eventId?: string | null;
    storageLocationId?: string | null;
};

/** Same logical ids as trip DAG / route (`storage:source:…`, event id, `storage:dest:…`). */
export function operationNodeToLogicalId(n: OperationDagNodeRow): string {
    if (n.kind === 'source') return `storage:source:${n.storageLocationId ?? ''}`;
    if (n.kind === 'dest') return `storage:dest:${n.storageLocationId ?? ''}`;
    return n.eventId ?? '';
}

export interface OperationSupplyFlowData {
    dagNodes: OperationDagNodeRow[];
    dagEdges: Array<{ fromNodeIndex: number; toNodeIndex: number }>;
    edgeFlowsByItemType: Record<string, LpEdgeFlowRow[]>;
    /** (itemTypeId, fromNodeIndex, toNodeIndex) -> taskId for persisting edits */
    taskIdByEdge: Map<string, string>;
    getNodeLabel: (nodeIndex: number) => string;
}

function edgeKey(from: number, to: number): string {
    return `${from}-${to}`;
}

/**
 * Build DAG and flows from operation. Node order: source storage, source events, target events, dest storage.
 */
export function buildOperationSupplyFlowData(operation: PackingOperation): OperationSupplyFlowData {
    const sourceStorageIds = (operation.sourceStorageLocations ?? []).map((s: { id: string }) => s.id);
    const sourceEventIds = (operation.sourceEvents ?? []).map((e: { id: string }) => e.id);
    const targetEventIds = (operation.targetEvents ?? []).map((e: { id: string }) => e.id);
    const targetStorageIds = (operation.targetStorageLocations ?? []).map((s: { id: string }) => s.id);

    const nodeCount = sourceStorageIds.length + sourceEventIds.length + targetEventIds.length + targetStorageIds.length;
    if (nodeCount === 0) {
        return {
            dagNodes: [],
            dagEdges: [],
            edgeFlowsByItemType: {},
            taskIdByEdge: new Map(),
            getNodeLabel: () => '',
        };
    }

    /** location key -> node index */
    const locationToIndex = new Map<string, number>();
    const dagNodes: OperationDagNodeRow[] = [];
    let idx = 0;
    for (const id of sourceStorageIds) {
        locationToIndex.set(`storage:source:${id}`, idx);
        dagNodes.push({ index: idx, kind: 'source', storageLocationId: id });
        idx++;
    }
    for (const id of sourceEventIds) {
        locationToIndex.set(`event:source:${id}`, idx);
        dagNodes.push({ index: idx, kind: 'event', eventId: id });
        idx++;
    }
    for (const id of targetEventIds) {
        locationToIndex.set(`event:target:${id}`, idx);
        dagNodes.push({ index: idx, kind: 'event', eventId: id });
        idx++;
    }
    for (const id of targetStorageIds) {
        locationToIndex.set(`storage:dest:${id}`, idx);
        dagNodes.push({ index: idx, kind: 'dest', storageLocationId: id });
        idx++;
    }

    function fromLocationToNodeIndex(fromEventId: string | null, fromStorageId: string | null): number | null {
        if (fromStorageId) {
            const k = `storage:source:${fromStorageId}`;
            return locationToIndex.get(k) ?? null;
        }
        if (fromEventId) {
            const k = `event:source:${fromEventId}`;
            const fromSourceIdx = locationToIndex.get(k);
            if (fromSourceIdx !== undefined) return fromSourceIdx;
            const kTarget = `event:target:${fromEventId}`;
            return locationToIndex.get(kTarget) ?? null;
        }
        return null;
    }

    function toLocationToNodeIndex(toEventId: string | null, toStorageId: string | null): number | null {
        if (toStorageId) {
            const k = `storage:dest:${toStorageId}`;
            return locationToIndex.get(k) ?? null;
        }
        if (toEventId) {
            const k = `event:target:${toEventId}`;
            const toTargetIdx = locationToIndex.get(k);
            if (toTargetIdx !== undefined) return toTargetIdx;
            const kSource = `event:source:${toEventId}`;
            return locationToIndex.get(kSource) ?? null;
        }
        return null;
    }

    const tasks = filterValidPackingTaskEndpoints((operation.packingTasks ?? []) as PackingTask[]);
    const edgeSet = new Set<string>();
    const edgeFlowsByItemType: Record<string, LpEdgeFlowRow[]> = {};
    const taskIdByEdge = new Map<string, string>();

    for (const task of tasks) {
        const itemTypeId = (task.itemType as { id: string } | null)?.id ?? null;
        if (!itemTypeId) continue;
        const fromEventId = (task.fromEvent as { id: string } | null)?.id ?? null;
        const fromStorageId = (task.fromStorageLocation as { id: string } | null)?.id ?? null;
        const toEventId = (task.toEvent as { id: string } | null)?.id ?? null;
        const toStorageId = (task.toStorageLocation as { id: string } | null)?.id ?? null;

        const fromIdx = fromLocationToNodeIndex(fromEventId, fromStorageId);
        const toIdx = toLocationToNodeIndex(toEventId, toStorageId);
        if (fromIdx == null || toIdx == null) continue;

        const key = edgeKey(fromIdx, toIdx);
        edgeSet.add(key);
        if (!edgeFlowsByItemType[itemTypeId]) edgeFlowsByItemType[itemTypeId] = [];
        edgeFlowsByItemType[itemTypeId].push({ fromNodeIndex: fromIdx, toNodeIndex: toIdx, value: task.quantity ?? 0 });
        taskIdByEdge.set(`${itemTypeId}\t${key}`, task.id);
    }

    const dagEdges: Array<{ fromNodeIndex: number; toNodeIndex: number }> = [];
    for (const k of Array.from(edgeSet)) {
        const [a, b] = k.split('-').map(Number);
        dagEdges.push({ fromNodeIndex: a, toNodeIndex: b });
    }

    const getNodeLabel = (nodeIndex: number): string => {
        const node = dagNodes[nodeIndex];
        if (!node) return '';
        if (node.kind === 'source' && node.storageLocationId) {
            const loc = (operation.sourceStorageLocations ?? []).find(
                (s: { id: string; name?: string }) => s.id === node.storageLocationId
            );
            return (loc as { name?: string })?.name ?? node.storageLocationId;
        }
        if (node.kind === 'dest' && node.storageLocationId) {
            const loc = (operation.targetStorageLocations ?? []).find(
                (s: { id: string; name?: string }) => s.id === node.storageLocationId
            );
            return (loc as { name?: string })?.name ?? node.storageLocationId;
        }
        if (node.kind === 'event' && node.eventId) {
            const events = [...(operation.sourceEvents ?? []), ...(operation.targetEvents ?? [])];
            const ev = events.find((e: { id: string }) => e.id === node.eventId);
            return (ev as { name?: string })?.name ?? node.eventId;
        }
        return '';
    };

    return {
        dagNodes,
        dagEdges,
        edgeFlowsByItemType,
        taskIdByEdge,
        getNodeLabel,
    };
}

/**
 * Resolve (fromNodeIndex, toNodeIndex) to location ids for creating a packing task.
 * Used when adding a new task from supply flow edit (zero → nonzero).
 */
export function edgeToTaskLocationIds(
    dagNodes: OperationDagNodeRow[],
    fromNodeIndex: number,
    toNodeIndex: number
): {
    fromEventId: string | null;
    fromStorageLocationId: string | null;
    toEventId: string | null;
    toStorageLocationId: string | null;
} {
    const fromNode = dagNodes[fromNodeIndex];
    const toNode = dagNodes[toNodeIndex];
    if (!fromNode || !toNode) {
        return {
            fromEventId: null,
            fromStorageLocationId: null,
            toEventId: null,
            toStorageLocationId: null,
        };
    }
    return {
        fromEventId: fromNode.kind === 'event' ? (fromNode.eventId ?? null) : null,
        fromStorageLocationId: fromNode.kind === 'source' ? (fromNode.storageLocationId ?? null) : null,
        toEventId: toNode.kind === 'event' ? (toNode.eventId ?? null) : null,
        toStorageLocationId: toNode.kind === 'dest' ? (toNode.storageLocationId ?? null) : null,
    };
}
